/**
 * Outil « Oxydoréduction » (Boîte à outils, 1re → Tle, physique-chimie).
 *
 * Demi-équations : un couple du programme, la méthode pas à pas (élément,
 * O avec H₂O, H avec H⁺, charge avec e⁻) ou « À toi » (l'élève règle les
 * coefficients et le côté de H₂O, H⁺, e⁻ ; le bilan élément par élément et la
 * charge se mettent à jour). Réaction : l'oxydant d'un couple et le réducteur
 * d'un autre ; on multiplie pour que les électrons s'éliminent, on additionne,
 * on simplifie ; relation à l'équivalence d'un titrage et couleurs. Défi en
 * cinq questions. Les formules sont lues en mots par le lecteur d'écran
 * (`speak`). Logique : `redox.ts`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import {
  Note,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  Segmented,
  Stepper,
  ToggleChip,
} from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { seededRng } from '../../ui/random';
import {
  CLASSIC_PAIRS,
  COUPLES,
  QUIZ_KINDS,
  SPECIES,
  balance,
  chargeText,
  classicLabel,
  coefIn,
  colorsOf,
  coupleById,
  coupleText,
  electrons,
  equivalence,
  exchangeText,
  halfEquation,
  halfSteps,
  halfText,
  halfVector,
  listText,
  makeQuiz,
  netText,
  rawText,
  ratioText,
  reaction,
  sameUpTo,
  scaledHalfText,
  sideText,
  speak,
  type Couple,
  type Side,
  type SpeciesId,
} from './redox';

const EQ = 'text-ink-900 text-[15px] font-semibold leading-relaxed';
const SELECT =
  'border-ink-200 text-ink-900 min-h-11 w-full rounded-xl border bg-white px-2 text-[15px]';

/** une formule à l'écran, en mots pour le lecteur d'écran */
function Chem({ text }: { text: string }) {
  return (
    <>
      <span aria-hidden="true">{frenchSpaces(text)}</span>
      <span className="sr-only">{speak(text)}</span>
    </>
  );
}

const coupleLabel = (c: Couple) =>
  `${coupleText(c)} — ${SPECIES[c.ox].name} / ${SPECIES[c.red].name}`;

function CoupleSelect({
  label,
  value,
  onChange,
  couples = COUPLES,
  show = coupleLabel,
}: {
  label: string;
  value: string;
  onChange: (id: string) => void;
  couples?: readonly Couple[];
  show?: (c: Couple) => string;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-ink-600 text-xs font-semibold">{label}</span>
      <select className={SELECT} value={value} onChange={(e) => onChange(e.target.value)}>
        {couples.map((c) => (
          <option key={c.id} value={c.id}>
            {show(c)}
          </option>
        ))}
      </select>
    </label>
  );
}

/** révèle des étapes une à une ; le bouton garde sa place (le focus reste) */
function useReveal(total: number, resetKey: string) {
  // la clé change (autre couple) : on repart de 1 dès ce rendu, sans effet
  const [state, setState] = useState({ key: resetKey, shown: 1 });
  const shown = state.key === resetKey ? Math.min(state.shown, total) : 1;
  return {
    shown,
    next: () => setState({ key: resetKey, shown: Math.min(total, shown + 1) }),
    all: () => setState({ key: resetKey, shown: total }),
    reset: () => setState({ key: resetKey, shown: 1 }),
    done: shown >= total,
  };
}

type StepView = { title: string; eq: string; say: string };

function StepList({ steps }: { steps: readonly StepView[] }) {
  return (
    <ol className="space-y-2" data-testid="rx-steps">
      {steps.map((s, i) => (
        <li key={i} className="border-ink-100 rounded-2xl border px-3 py-2">
          <p className="text-ink-500 text-xs font-semibold">
            <Chem text={`${i + 1}. ${s.title}`} />
          </p>
          <p className={EQ}>
            <Chem text={s.eq} />
          </p>
          <p className="text-ink-700 text-sm">
            <Chem text={s.say} />
          </p>
        </li>
      ))}
    </ol>
  );
}

/** annonce l'étape qui vient d'apparaître */
function StepLive({ step }: { step: StepView | undefined }) {
  return (
    <p className="sr-only" aria-live="polite">
      {step ? speak(`${step.eq}. ${step.say}`) : ''}
    </p>
  );
}

function RevealButtons({ r }: { r: ReturnType<typeof useReveal> }) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        className={cn(PRIMARY_BUTTON, r.done && 'opacity-50')}
        aria-disabled={r.done}
        onClick={r.next}
      >
        Étape suivante
      </button>
      <button type="button" className={SECONDARY_BUTTON} onClick={r.all}>
        Tout voir
      </button>
      <button type="button" className={SECONDARY_BUTTON} onClick={r.reset}>
        Recommencer
      </button>
    </div>
  );
}

/* ---------- demi-équations ---------- */

function HalfGuided({ c, states }: { c: Couple; states: boolean }) {
  const steps = halfSteps(c, states);
  const r = useReveal(steps.length, c.id);
  const he = halfEquation(c);
  const v = halfVector(he);
  return (
    <div className="space-y-3">
      <StepList steps={steps.slice(0, r.shown)} />
      <StepLive step={steps[r.shown - 1]} />
      <RevealButtons r={r} />
      {r.done && (
        <Note live={false} tone="success">
          <span data-testid="rx-half-final">
            <Chem
              text={`Demi-équation du couple ${coupleText(c)} : ${halfText(he, states)}. Dans ce sens, ${exchangeText(v[c.ox]!, c.ox, he.n, false)} : c’est une réduction. Lue de droite à gauche, ${exchangeText(-v[c.red]!, c.red, he.n, true)} : c’est une oxydation.`}
            />
          </span>
        </Note>
      )}
    </div>
  );
}

type Where = 'left' | 'right';
const SIDES: ReadonlyArray<{ value: Where; label: string }> = [
  { value: 'left', label: 'à gauche' },
  { value: 'right', label: 'à droite' },
];

/** H₂O, H⁺ ou e⁻ : combien, et de quel côté */
function Extra({
  label,
  srLabel,
  sideLabel,
  value,
  onValue,
  side,
  onSide,
  max,
}: {
  label: string;
  srLabel: string;
  sideLabel: string;
  value: number;
  onValue: (v: number) => void;
  side: Where;
  onSide: (s: Where) => void;
  max: number;
}) {
  return (
    <div className="space-y-1">
      <Stepper label={label} srLabel={srLabel} value={value} min={0} max={max} onChange={onValue} />
      <div className="flex justify-center">
        <Segmented ariaLabel={sideLabel} value={side} options={SIDES} onChange={onSide} />
      </div>
    </div>
  );
}

function HalfYours({ c, states }: { c: Couple; states: boolean }) {
  const he = halfEquation(c);
  const [a, setA] = useState(1);
  const [b, setB] = useState(1);
  const [w, setW] = useState(0);
  const [wSide, setWSide] = useState<Where>('left');
  const [h, setH] = useState(0);
  const [hSide, setHSide] = useState<Where>('left');
  const [n, setN] = useState(0);
  const [nSide, setNSide] = useState<Where>('left');
  // l'espèce du couple est l'eau ou H⁺ : son coefficient suffit
  const hasW = c.ox === 'H2O' || c.red === 'H2O';
  const hasH = c.ox === 'H+' || c.red === 'H+';
  const sign = (s: Where) => (s === 'left' ? 1 : -1);
  const v: Side = {};
  const put = (k: SpeciesId | 'e', x: number) => {
    if (!x) return;
    const y = (v[k] ?? 0) + x;
    if (y) v[k] = y;
    else delete v[k];
  };
  put(c.ox, a);
  put(c.red, -b);
  if (!hasW) put('H2O', sign(wSide) * w);
  if (!hasH) put('H+', sign(hSide) * h);
  put('e', sign(nSide) * n);
  const bal = balance(v);
  const target = halfVector(he);
  const exactly = sameUpTo(v, target, 1);
  const multiple = [2, 3, 4].find((m) => sameUpTo(v, target, m));
  const bad = bal.rows
    .filter((r) => !r.ok)
    .map((r) => (r.label === 'charge' ? 'la charge' : `l’élément ${r.label}`));
  const verdict = exactly
    ? `Bravo : ${halfText(he, states)} est équilibrée (éléments et charge).`
    : multiple
      ? `Équilibrée, mais tous les coefficients sont multiples de ${multiple} : simplifie.`
      : bal.ok
        ? 'Équilibrée, mais ce n’est pas la demi-équation attendue : compare avec la méthode pas à pas.'
        : `Pas encore : ${listText(bad)} ${
            bad.length > 1
              ? 'ne sont pas conservés'
              : bad[0] === 'la charge'
                ? 'n’est pas conservée'
                : 'n’est pas conservé'
          }.`;
  return (
    <div className="space-y-3">
      <p className={EQ} data-testid="rx-yours">
        <Chem text={sideText(v, states)} />
      </p>
      <p className="sr-only" aria-live="polite">
        {speak(`${sideText(v, states)}. ${verdict}`)}
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Stepper
          label={`Coefficient de ${SPECIES[c.ox].text}`}
          srLabel={`Coefficient (${SPECIES[c.ox].one})`}
          value={a}
          min={1}
          max={4}
          onChange={setA}
        />
        <Stepper
          label={`Coefficient de ${SPECIES[c.red].text}`}
          srLabel={`Coefficient (${SPECIES[c.red].one})`}
          value={b}
          min={1}
          max={4}
          onChange={setB}
        />
        {!hasW && (
          <Extra
            label="Eau H₂O"
            srLabel="Molécules d’eau"
            sideLabel="Côté de l’eau"
            value={w}
            onValue={setW}
            side={wSide}
            onSide={setWSide}
            max={8}
          />
        )}
        {!hasH && (
          <Extra
            label="Ions H⁺"
            srLabel="Ions hydrogène"
            sideLabel="Côté des ions H⁺"
            value={h}
            onValue={setH}
            side={hSide}
            onSide={setHSide}
            max={16}
          />
        )}
        <Extra
          label="Électrons e⁻"
          srLabel="Électrons"
          sideLabel="Côté des électrons"
          value={n}
          onValue={setN}
          side={nSide}
          onSide={setNSide}
          max={8}
        />
      </div>
      <table className="w-full text-center text-sm" data-testid="rx-balance">
        <caption className="text-ink-500 pb-1 text-left text-xs font-semibold">
          Bilan : atomes et charge de chaque côté
        </caption>
        <thead className="text-ink-500 text-xs">
          <tr>
            <th scope="col" className="py-1 text-left font-semibold">
              <span className="sr-only">Élément</span>
            </th>
            <th scope="col" className="py-1 font-semibold">
              à gauche
            </th>
            <th scope="col" className="py-1 font-semibold">
              à droite
            </th>
            <th scope="col" className="py-1 font-semibold">
              <span className="sr-only">conservé ?</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {bal.rows.map((r) => (
            <tr key={r.label} className="border-ink-100 border-t">
              <th scope="row" className="text-ink-700 py-1 text-left font-semibold">
                {r.label === 'charge' ? 'Charge' : r.label}
              </th>
              <td className="text-ink-900 py-1 tabular-nums">
                {r.label === 'charge' ? chargeText(r.left) : r.left}
              </td>
              <td className="text-ink-900 py-1 tabular-nums">
                {r.label === 'charge' ? chargeText(r.right) : r.right}
              </td>
              <td className="py-1">
                <span
                  className={cn(
                    'text-ink-900 inline-block min-w-7 rounded-md px-1 font-bold',
                    r.ok ? 'bg-success-50' : 'bg-error-50',
                  )}
                >
                  {r.ok ? '✓' : '✗'}
                  <span className="sr-only">{r.ok ? ' conservé' : ' non conservé'}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Note live={false} tone={exactly ? 'success' : bal.ok ? 'neutral' : 'error'}>
        <span data-testid="rx-verdict">
          <Chem text={verdict} />
        </span>
      </Note>
    </div>
  );
}

function HalfMode({ states }: { states: boolean }) {
  const [id, setId] = useState('MnO4/Mn2+');
  const [sub, setSub] = useState<'guided' | 'yours'>('guided');
  const c = coupleById(id);
  return (
    <div className="space-y-3">
      <CoupleSelect label="Couple oxydant / réducteur" value={id} onChange={setId} />
      <Segmented
        ariaLabel="Qui équilibre"
        value={sub}
        options={[
          { value: 'guided', label: 'Pas à pas' },
          { value: 'yours', label: 'À toi' },
        ]}
        onChange={setSub}
      />
      {sub === 'guided' ? (
        <HalfGuided c={c} states={states} />
      ) : (
        <HalfYours key={c.id} c={c} states={states} />
      )}
      <p className="text-ink-500 text-xs">
        <Chem text="Méthode (milieu acide), dans l’ordre : conserver l’élément autre que O et H, puis O en ajoutant H₂O, puis H en ajoutant H⁺, enfin la charge en ajoutant des électrons." />
      </p>
    </div>
  );
}

/* ---------- réaction ---------- */

const OX_COUPLES = COUPLES.filter((c) => c.oxUsable);
const RED_COUPLES = COUPLES.filter((c) => c.redUsable);

function ReactionMode({ states }: { states: boolean }) {
  const [ia, setIa] = useState('MnO4/Mn2+');
  const [ib, setIb] = useState('Fe3+/Fe2+');
  const A = coupleById(ia);
  const B = coupleById(ib);
  const same = ia === ib;
  const r = same ? null : reaction(A, B);
  const classic = CLASSIC_PAIRS.findIndex((p) => p.a === ia && p.b === ib);
  const pair = classic >= 0 ? CLASSIC_PAIRS[classic]! : null;
  const ox = SPECIES[A.ox].text;
  const red = SPECIES[B.red].text;
  const steps: StepView[] =
    r && r.ok
      ? [
          {
            title: `Réduction de ${ox}`,
            eq: scaledHalfText(r.A, r.kA, 'reduction', states),
            say: `${exchangeText(coefIn(r.A, A.ox), A.ox, r.A.n, false)}${
              r.kA > 1
                ? ` ; on multiplie par ${r.kA} : ${electrons(r.N)} capté${r.N > 1 ? 's' : ''}`
                : ''
            }.`,
          },
          {
            title: `Oxydation de ${red}`,
            eq: scaledHalfText(r.B, r.kB, 'oxidation', states),
            say: `Demi-équation écrite dans le sens de l’oxydation : ${exchangeText(coefIn(r.B, B.red), B.red, r.B.n, true)}${
              r.kB > 1 ? ` ; on multiplie par ${r.kB}` : ''
            }. Autant d’électrons cédés que captés : ${r.N}.`,
          },
          {
            title: 'On additionne',
            eq: rawText(r, states),
            say: `${r.N > 1 ? `Les ${r.N} électrons s’éliminent` : 'L’électron s’élimine'} : il n’y a jamais d’électrons dans l’équation d’une réaction.`,
          },
          {
            title: 'On simplifie',
            eq: netText(r, states),
            say: [
              r.common.length > 0
                ? `${listText(r.common.map((k) => SPECIES[k].text))} ${r.common.length > 1 ? 'sont présents' : 'est présent'} des deux côtés : on simplifie.`
                : 'Rien d’autre à simplifier.',
              r.div > 1 ? `On divise tous les coefficients par ${r.div}.` : '',
              'On vérifie : éléments et charge conservés.',
            ]
              .filter(Boolean)
              .join(' '),
          },
        ]
      : [];
  const rev = useReveal(steps.length || 1, `${ia}|${ib}`);
  const lost = r
    ? [(r.net[A.ox] ?? 0) > 0 ? '' : ox, (r.net[B.red] ?? 0) > 0 ? '' : red].filter(Boolean)
    : [];
  const eqv = r ? equivalence(r) : null;
  const colors = r ? colorsOf(r) : [];
  // un titrage met en jeu deux espèces dissoutes (pas Zn solide, ni une dismutation)
  const titratable =
    A.ox !== B.red && SPECIES[A.ox].state === 'aq' && SPECIES[B.red].state === 'aq';
  return (
    <div className="space-y-3">
      <label className="block space-y-1">
        <span className="text-ink-600 text-xs font-semibold">Réaction classique</span>
        <select
          className={SELECT}
          value={classic >= 0 ? String(classic) : ''}
          onChange={(e) => {
            const p = CLASSIC_PAIRS[Number(e.target.value)];
            if (e.target.value !== '' && p) {
              setIa(p.a);
              setIb(p.b);
            }
          }}
        >
          <option value="">Autre : choisis les deux couples</option>
          {CLASSIC_PAIRS.map((p, i) => (
            <option key={`${p.a}|${p.b}`} value={String(i)}>
              {classicLabel(p)}
            </option>
          ))}
        </select>
      </label>
      <CoupleSelect
        label="L’oxydant qui réagit (couple)"
        value={ia}
        onChange={setIa}
        couples={OX_COUPLES}
        show={(c) => `${SPECIES[c.ox].text} — ${SPECIES[c.ox].one}, couple ${coupleText(c)}`}
      />
      <CoupleSelect
        label="Le réducteur qui réagit (couple)"
        value={ib}
        onChange={setIb}
        couples={RED_COUPLES}
        show={(c) => `${SPECIES[c.red].text} — ${SPECIES[c.red].one}, couple ${coupleText(c)}`}
      />
      {same || !r ? (
        <Note live tone="error">
          Choisis deux couples différents.
        </Note>
      ) : !r.ok ? (
        <Note live tone="error">
          <span data-testid="rx-incoherent">
            <Chem
              text={`Avec ces deux couples, l’équation simplifiée ne garde pas ${listText(lost)} parmi les réactifs : cette réaction n’a pas de sens. Choisis d’autres couples.`}
            />
          </span>
        </Note>
      ) : (
        <>
          <p className="text-ink-800 text-sm">
            <Chem
              text={`${ox} (oxydant du couple ${coupleText(A)}) réagit avec ${red} (réducteur du couple ${coupleText(B)}).`}
            />
          </p>
          {pair?.note && (
            <Note live={false} tone="neutral">
              <Chem text={pair.note} />
            </Note>
          )}
          <StepList steps={steps.slice(0, rev.shown)} />
          <StepLive step={steps[rev.shown - 1]} />
          <RevealButtons r={rev} />
          {rev.done && (
            <div className="space-y-2" data-testid="rx-after">
              {eqv && titratable && (
                <p className="text-ink-800 text-sm">
                  <Chem
                    text={`Pour un titrage, à l’équivalence, les réactifs ont été introduits dans les proportions stœchiométriques : ${ratioText(eqv.ox, eqv.a, eqv.red, eqv.b)}.`}
                  />
                </p>
              )}
              {colors.length > 0 && (
                <p className="text-ink-800 text-sm">
                  <Chem
                    text={`Couleurs en solution : ${colors.join(' ; ')}. Les autres espèces dissoutes sont pratiquement incolores.`}
                  />
                </p>
              )}
            </div>
          )}
        </>
      )}
      <p className="text-ink-500 text-xs">
        <Chem text="En 1re, l’énoncé dit quel oxydant réagit avec quel réducteur : l’outil ne prédit pas si la réaction a lieu. Les ions spectateurs (K⁺ du permanganate de potassium, Na⁺, SO₄²⁻ de l’acide sulfurique) n’apparaissent pas dans l’équation." />
      </p>
    </div>
  );
}

/* ---------- défi ---------- */

function Challenge({ seed, states }: { seed?: number; states: boolean }) {
  const rnd = useMemo(() => (seed === undefined ? Math.random : seededRng(seed)), [seed]);
  const draw = () => Math.floor(rnd() * 2 ** 31);
  const rounds = QUIZ_KINDS.length;
  const [count, setCount] = useState(1);
  // la graine de la question : changer « États physiques » la réécrit sans la changer
  const [qSeed, setQSeed] = useState(draw);
  const kind = QUIZ_KINDS[count - 1]!;
  const q = useMemo(() => makeQuiz(kind, seededRng(qSeed), states), [kind, qSeed, states]);
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const titleRef = useRef<HTMLParagraphElement>(null);
  const moved = useRef(false);
  const done = picked !== null && count >= rounds;
  const chosen = picked === null ? null : q.options[picked]!;
  const right = q.options.find((o) => o.correct)!;

  useEffect(() => {
    if (moved.current) titleRef.current?.focus();
    moved.current = false;
  }, [q]);

  const choose = (i: number) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.options[i]!.correct) setScore(score + 1);
  };
  const next = () => {
    const c = done ? 1 : count + 1;
    if (done) setScore(0);
    setCount(c);
    moved.current = true;
    setQSeed(draw());
    setPicked(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
        {frenchSpaces(`Question ${count} / ${rounds} · ${score} juste${score > 1 ? 's' : ''}`)}
      </p>
      <p ref={titleRef} tabIndex={-1} className="text-ink-900 text-base font-semibold outline-none">
        <Chem text={q.question} />
      </p>
      <div role="group" aria-label="Réponses" className="grid grid-cols-1 gap-2">
        {q.options.map((o, i) => (
          <button
            key={o.text}
            type="button"
            onClick={() => choose(i)}
            aria-pressed={picked === i}
            aria-disabled={picked !== null && picked !== i}
            className={cn(
              'min-h-11 rounded-xl border px-3 py-2 text-left text-[15px] font-semibold transition-colors',
              picked === null && 'border-ink-200 text-ink-800 hover:bg-ink-50',
              picked !== null && o.correct && 'border-success-600 bg-success-50 text-ink-900',
              picked === i && !o.correct && 'border-error-500 bg-error-50 text-ink-900',
              picked !== null && picked !== i && !o.correct && 'border-ink-100 text-ink-500',
            )}
          >
            <Chem text={o.text} />
          </button>
        ))}
      </div>
      {chosen && (
        <Note live tone={chosen.correct ? 'success' : 'error'}>
          <span data-testid="rx-feedback">
            <Chem
              text={
                chosen.correct
                  ? `Exact. ${q.explain}`
                  : `${chosen.hint ?? ''} La bonne réponse : ${right.text}. ${q.explain}`
              }
            />
          </span>
        </Note>
      )}
      {picked !== null && (
        <div className="flex flex-wrap items-center gap-3">
          {done && (
            <p className="text-ink-900 text-sm font-semibold">
              {frenchSpaces(`Score : ${score} / ${rounds}`)}
            </p>
          )}
          <button type="button" className={done ? SECONDARY_BUTTON : PRIMARY_BUTTON} onClick={next}>
            {done ? 'Recommencer' : 'Question suivante'}
          </button>
        </div>
      )}
    </div>
  );
}

export function RedoxTool({ seed }: { /** graine du défi (tests) */ seed?: number }) {
  const [mode, setMode] = useState<'half' | 'reaction' | 'quiz'>('half');
  const [states, setStates] = useState(true);
  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Ce qu’on fait"
        value={mode}
        options={[
          { value: 'half', label: 'Demi-équations' },
          { value: 'reaction', label: 'Réaction' },
          { value: 'quiz', label: 'Défi' },
        ]}
        onChange={setMode}
      />
      <ToggleChip on={states} onClick={() => setStates(!states)}>
        {frenchSpaces('États physiques (aq), (s)…')}
      </ToggleChip>
      <div hidden={mode !== 'half'}>
        <HalfMode states={states} />
      </div>
      <div hidden={mode !== 'reaction'}>
        <ReactionMode states={states} />
      </div>
      <div hidden={mode !== 'quiz'}>
        <Challenge seed={seed} states={states} />
      </div>
    </div>
  );
}
